import React, { useState, useRef, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Tabs, Tab, IconButton, Tooltip, Divider } from '@material-ui/core';
import CodeIcon from '@material-ui/icons/Code';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import FormatBoldIcon from '@material-ui/icons/FormatBold';
import FormatItalicIcon from '@material-ui/icons/FormatItalic';
import FormatUnderlinedIcon from '@material-ui/icons/FormatUnderlined';
import FormatColorTextIcon from '@material-ui/icons/FormatColorText';
import FormatColorFillIcon from '@material-ui/icons/FormatColorFill';
import ImageIcon from '@material-ui/icons/Image';
import LinkIcon from '@material-ui/icons/Link';

const useStyles = makeStyles((theme) => ({
    editorContainer: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
    },
    tabsContainer: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
    },
    tabPanel: {
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    toolbar: {
        display: 'flex',
        gap: theme.spacing(0.5),
        padding: theme.spacing(1),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
        flexWrap: 'wrap',
    },
    visualEditorContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(2),
    },
    visualEditorIframe: {
        width: '100%',
        minHeight: '600px',
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    codeEditor: {
        flex: 1,
        width: '100%',
        minHeight: '600px',
        padding: theme.spacing(2),
        fontFamily: '"Fira Code", "Courier New", monospace',
        fontSize: '14px',
        lineHeight: '1.8',
        border: 'none',
        outline: 'none',
        resize: 'vertical',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        overflowY: 'auto',
        whiteSpace: 'pre-wrap',
        wordWrap: 'break-word',
        '&:focus': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: '-2px',
        },
        '&::-webkit-scrollbar': {
            width: '10px',
        },
        '&::-webkit-scrollbar-track': {
            background: theme.palette.background.default,
        },
        '&::-webkit-scrollbar-thumb': {
            background: theme.palette.divider,
            borderRadius: '5px',
        },
    },
    previewContainer: {
        flex: 1,
        overflow: 'auto',
        backgroundColor: '#f5f5f5',
        padding: theme.spacing(2),
    },
    previewIframe: {
        width: '100%',
        minHeight: '600px',
        border: 'none',
        backgroundColor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    },
    tabLabel: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
}));

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{ flex: 1, display: value === index ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden' }}
            {...other}
        >
            {value === index && children}
        </div>
    );
}

const EmailEditor = ({ value, onChange, readOnly }) => {
    const classes = useStyles();
    const [activeTab, setActiveTab] = useState(0);
    const [htmlContent, setHtmlContent] = useState(value || '');
    const visualEditorRef = useRef(null);
    const isInitializedRef = useRef(false);

    useEffect(() => {
        setHtmlContent(value || '');
    }, [value]);

    useEffect(() => {
        if (activeTab === 0 && visualEditorRef.current && htmlContent) {
            const iframe = visualEditorRef.current;
            const doc = iframe.contentDocument || iframe.contentWindow.document;

            // Only initialize once or when switching tabs
            if (!isInitializedRef.current) {
                doc.open();
                doc.write(htmlContent);
                doc.close();
                isInitializedRef.current = true;
            }

            if (!readOnly) {
                doc.body.contentEditable = true;
                doc.body.style.outline = 'none';

                // Add context menu for changing background color of elements
                doc.addEventListener('contextmenu', (e) => {
                    e.preventDefault();
                    const target = e.target;

                    // Create context menu
                    const existingMenu = doc.getElementById('custom-context-menu');
                    if (existingMenu) existingMenu.remove();

                    const menu = doc.createElement('div');
                    menu.id = 'custom-context-menu';
                    menu.style.cssText = `
                        position: absolute;
                        left: ${e.pageX}px;
                        top: ${e.pageY}px;
                        background: white;
                        border: 1px solid #ccc;
                        border-radius: 4px;
                        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
                        padding: 8px 0;
                        z-index: 10000;
                        font-family: Arial, sans-serif;
                        font-size: 14px;
                        max-width: 400px;
                    `;

                    // Get element hierarchy (element + parents)
                    const hierarchy = [];
                    let current = target;
                    while (current && current !== doc.body) {
                        const tagName = current.tagName.toLowerCase();
                        const className = current.className ? `.${current.className.split(' ')[0]}` : '';
                        const id = current.id ? `#${current.id}` : '';
                        hierarchy.push({
                            element: current,
                            label: `${tagName}${id}${className}`
                        });
                        current = current.parentElement;
                    }

                    // Add title
                    const title = doc.createElement('div');
                    title.textContent = '🎨 Escolha o que alterar:';
                    title.style.cssText = `
                        padding: 8px 16px;
                        font-weight: bold;
                        border-bottom: 1px solid #eee;
                        color: #333;
                    `;
                    menu.appendChild(title);

                    // Section 1: Text Color
                    const textColorTitle = doc.createElement('div');
                    textColorTitle.textContent = '📝 Cor do Texto:';
                    textColorTitle.style.cssText = `
                        padding: 6px 16px;
                        font-size: 12px;
                        color: #666;
                        background: #f9f9f9;
                    `;
                    menu.appendChild(textColorTitle);

                    hierarchy.forEach((item, index) => {
                        const menuItem = doc.createElement('div');
                        const indent = '  '.repeat(index);
                        menuItem.textContent = `${indent}${index === 0 ? '→ ' : '↑ '}${item.label}`;
                        menuItem.style.cssText = `
                            padding: 6px 16px;
                            cursor: pointer;
                            white-space: nowrap;
                            font-family: monospace;
                            font-size: 13px;
                        `;
                        menuItem.onmouseover = () => {
                            menuItem.style.backgroundColor = '#e3f2fd';
                            item.element.style.outline = '2px dashed #2196f3';
                        };
                        menuItem.onmouseout = () => {
                            menuItem.style.backgroundColor = 'transparent';
                            item.element.style.outline = '';
                        };
                        menuItem.onclick = () => {
                            const input = document.createElement('input');
                            input.type = 'color';
                            input.value = '#000000';
                            input.onchange = (event) => {
                                item.element.style.setProperty('color', event.target.value, 'important');
                                item.element.style.outline = '';
                                syncContent();
                            };
                            input.click();
                            menu.remove();
                        };
                        menu.appendChild(menuItem);
                    });

                    // Section 2: Background Color
                    const bgColorTitle = doc.createElement('div');
                    bgColorTitle.textContent = '🎨 Cor de Fundo:';
                    bgColorTitle.style.cssText = `
                        padding: 6px 16px;
                        font-size: 12px;
                        color: #666;
                        background: #f9f9f9;
                        margin-top: 4px;
                    `;
                    menu.appendChild(bgColorTitle);

                    hierarchy.forEach((item, index) => {
                        const menuItem = doc.createElement('div');
                        const indent = '  '.repeat(index);
                        menuItem.textContent = `${indent}${index === 0 ? '→ ' : '↑ '}${item.label}`;
                        menuItem.style.cssText = `
                            padding: 6px 16px;
                            cursor: pointer;
                            white-space: nowrap;
                            font-family: monospace;
                            font-size: 13px;
                        `;
                        menuItem.onmouseover = () => {
                            menuItem.style.backgroundColor = '#fff3e0';
                            item.element.style.outline = '2px dashed #ff9800';
                        };
                        menuItem.onmouseout = () => {
                            menuItem.style.backgroundColor = 'transparent';
                            item.element.style.outline = '';
                        };
                        menuItem.onclick = () => {
                            const input = document.createElement('input');
                            input.type = 'color';
                            input.value = '#ffffff';
                            input.onchange = (event) => {
                                item.element.style.setProperty('background-color', event.target.value, 'important');
                                item.element.style.setProperty('background', event.target.value, 'important');
                                item.element.style.outline = '';
                                syncContent();
                            };
                            input.click();
                            menu.remove();
                        };
                        menu.appendChild(menuItem);
                    });

                    // Section 3: Add/Edit Link
                    const linkTitle = doc.createElement('div');
                    linkTitle.textContent = '🔗 Adicionar/Editar Link:';
                    linkTitle.style.cssText = `
                        padding: 6px 16px;
                        font-size: 12px;
                        color: #666;
                        background: #f9f9f9;
                        margin-top: 4px;
                    `;
                    menu.appendChild(linkTitle);

                    hierarchy.forEach((item, index) => {
                        const menuItem = doc.createElement('div');
                        const indent = '  '.repeat(index);

                        // Check if element already has a link
                        const currentHref = item.element.tagName.toLowerCase() === 'a'
                            ? item.element.href
                            : (item.element.closest('a')?.href || '');
                        const hasLink = currentHref !== '';

                        menuItem.textContent = `${indent}${index === 0 ? '→ ' : '↑ '}${item.label}${hasLink ? ' (tem link)' : ''}`;
                        menuItem.style.cssText = `
                            padding: 6px 16px;
                            cursor: pointer;
                            white-space: nowrap;
                            font-family: monospace;
                            font-size: 13px;
                        `;
                        menuItem.onmouseover = () => {
                            menuItem.style.backgroundColor = '#e8f5e9';
                            item.element.style.outline = '2px dashed #4caf50';
                        };
                        menuItem.onmouseout = () => {
                            menuItem.style.backgroundColor = 'transparent';
                            item.element.style.outline = '';
                        };
                        menuItem.onclick = () => {
                            const url = prompt('Digite a URL do link (ex: https://exemplo.com):', currentHref);
                            if (url !== null) {
                                if (url === '') {
                                    // Remove link
                                    if (item.element.tagName.toLowerCase() === 'a') {
                                        const parent = item.element.parentNode;
                                        while (item.element.firstChild) {
                                            parent.insertBefore(item.element.firstChild, item.element);
                                        }
                                        parent.removeChild(item.element);
                                    }
                                } else {
                                    // Add or update link
                                    if (item.element.tagName.toLowerCase() === 'a') {
                                        item.element.href = url;
                                    } else {
                                        const link = doc.createElement('a');
                                        link.href = url;
                                        link.style.cssText = item.element.style.cssText;
                                        item.element.parentNode.insertBefore(link, item.element);
                                        link.appendChild(item.element);
                                    }
                                }
                                item.element.style.outline = '';
                                syncContent();
                            }
                            menu.remove();
                        };
                        menu.appendChild(menuItem);
                    });

                    doc.body.appendChild(menu);

                    // Remove menu on click outside
                    setTimeout(() => {
                        doc.addEventListener('click', () => {
                            // Remove outlines
                            hierarchy.forEach(item => item.element.style.outline = '');
                            if (menu.parentNode) menu.remove();
                        }, { once: true });
                    }, 100);
                });

                // Make images resizable
                const images = doc.querySelectorAll('img');
                images.forEach(img => {
                    img.style.cursor = 'pointer';
                    img.setAttribute('contenteditable', 'false');

                    img.onclick = (e) => {
                        e.preventDefault();
                        const currentWidth = img.style.width || img.width + 'px';
                        const newWidth = prompt('Digite a largura da imagem (ex: 300px ou 50%):', currentWidth);
                        if (newWidth !== null && newWidth !== '') {
                            img.style.width = newWidth;
                            syncContent();
                        }
                    };
                });

                // Sync changes back to state
                const syncContent = () => {
                    const updatedHtml = doc.documentElement.outerHTML;
                    setHtmlContent(updatedHtml);
                    onChange(updatedHtml);
                };

                // Use debounced input to avoid re-rendering on every keystroke
                let typingTimer;
                const handleInput = () => {
                    clearTimeout(typingTimer);
                    typingTimer = setTimeout(syncContent, 1500); // Sync after 1.5 seconds of no typing
                };

                // Sync on blur (when user clicks outside)
                const handleBlur = () => {
                    clearTimeout(typingTimer);
                    syncContent();
                };

                doc.body.addEventListener('input', handleInput);
                doc.body.addEventListener('blur', handleBlur, true);

                return () => {
                    doc.body.removeEventListener('input', handleInput);
                    doc.body.removeEventListener('blur', handleBlur, true);
                };
            }
        }
    }, [activeTab, readOnly]); // Removed htmlContent and onChange from dependencies

    // Reset initialization when switching tabs
    useEffect(() => {
        if (activeTab !== 0) {
            isInitializedRef.current = false;
        }
    }, [activeTab]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleCodeChange = (event) => {
        if (!readOnly) {
            const newHtml = event.target.value;
            setHtmlContent(newHtml);
            onChange(newHtml);
            isInitializedRef.current = false; // Force re-init when code changes
        }
    };

    const execCommand = (command, value = null) => {
        if (visualEditorRef.current && !readOnly) {
            const iframe = visualEditorRef.current;
            const doc = iframe.contentDocument || iframe.contentWindow.document;
            doc.execCommand(command, false, value);
        }
    };

    const handleImageUpload = () => {
        if (readOnly) return;

        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    execCommand('insertImage', event.target.result);
                };
                reader.readAsDataURL(file);
            }
        };
        input.click();
    };

    const handleBackgroundColor = () => {
        if (readOnly) return;
        const color = prompt('Digite a cor de fundo (ex: #ff0000, red, ou rgb(255,0,0)):');
        if (color) {
            execCommand('backColor', color);
        }
    };

    const handleCreateLink = () => {
        if (readOnly) return;
        const url = prompt('Digite a URL do link (ex: https://exemplo.com):');
        if (url) {
            execCommand('createLink', url);
        }
    };

    return (
        <div className={classes.editorContainer}>
            <Tabs
                value={activeTab}
                onChange={handleTabChange}
                className={classes.tabsContainer}
                indicatorColor="primary"
                textColor="primary"
            >
                <Tab
                    label={
                        <div className={classes.tabLabel}>
                            <EditIcon fontSize="small" />
                            <span>Editor Visual</span>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={classes.tabLabel}>
                            <CodeIcon fontSize="small" />
                            <span>Código HTML</span>
                        </div>
                    }
                />
                <Tab
                    label={
                        <div className={classes.tabLabel}>
                            <VisibilityIcon fontSize="small" />
                            <span>Preview</span>
                        </div>
                    }
                />
            </Tabs>

            <TabPanel value={activeTab} index={0}>
                {!readOnly && (
                    <div className={classes.toolbar}>
                        <Tooltip title="Negrito (Ctrl+B)">
                            <IconButton size="small" onClick={() => execCommand('bold')}>
                                <FormatBoldIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Itálico (Ctrl+I)">
                            <IconButton size="small" onClick={() => execCommand('italic')}>
                                <FormatItalicIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Sublinhado (Ctrl+U)">
                            <IconButton size="small" onClick={() => execCommand('underline')}>
                                <FormatUnderlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                        <Tooltip title="Cor do Texto">
                            <IconButton size="small" onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'color';
                                input.value = '#000000';
                                input.onchange = (e) => execCommand('foreColor', e.target.value);
                                input.click();
                            }}>
                                <FormatColorTextIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Cor de Fundo">
                            <IconButton size="small" onClick={() => {
                                const input = document.createElement('input');
                                input.type = 'color';
                                input.value = '#ffff00';
                                input.onchange = (e) => execCommand('backColor', e.target.value);
                                input.click();
                            }}>
                                <FormatColorFillIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Divider orientation="vertical" flexItem />
                        <Tooltip title="Criar Link (selecione o texto primeiro)">
                            <IconButton size="small" onClick={handleCreateLink}>
                                <LinkIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Inserir/Substituir Imagem (clique na imagem para redimensionar)">
                            <IconButton size="small" onClick={handleImageUpload}>
                                <ImageIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </div>
                )}
                <div className={classes.visualEditorContainer}>
                    <iframe
                        ref={visualEditorRef}
                        className={classes.visualEditorIframe}
                        title="Visual Editor"
                    />
                </div>
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
                <textarea
                    className={classes.codeEditor}
                    value={htmlContent}
                    onChange={handleCodeChange}
                    readOnly={readOnly}
                    placeholder="Cole aqui o código HTML do seu template de email..."
                    spellCheck={false}
                />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
                <div className={classes.previewContainer}>
                    <iframe
                        className={classes.previewIframe}
                        srcDoc={htmlContent || '<p style="text-align: center; color: #999; padding: 40px;">Nenhum conteúdo para visualizar</p>'}
                        title="Email Preview"
                        sandbox="allow-same-origin allow-scripts"
                    />
                </div>
            </TabPanel>
        </div>
    );
};

export default EmailEditor;
